import { FC, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import GetAppIcon from "@mui/icons-material/GetApp";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import DeleteIcon from "@mui/icons-material/Delete";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

import { WithAnyRole } from "src/components/Authorization";

import Button from "../../components/Button";
import Confirm from "../../components/Confirm";
import TableComponent from "../../components/TableComponent";
import { formatDate } from "../../services/formatters";
import { ROLE_CODES } from "../../constants";
import { CertificateStatusType } from "../../services/certificateStatus";
import request from "../../api/request";
import { openDownloadWindow, openExportWindow } from "../../api";
import { COMPUTED_ROLES } from "../../constants";
import HistoryService, { HistoryItem } from "./service";
import type { CourseCertificate, CertificateMap } from "../../api/compliance";

interface CourseHistoryProps {
  userId: number;
  certificate: CourseCertificate;
  certificateMap: CertificateMap;
  onClose: () => void;
  onDeleteSuccess?: () => void;
  className?: string;
}

const CourseHistory: FC<CourseHistoryProps> = ({
  userId,
  certificate,
  certificateMap,
  onClose,
  onDeleteSuccess,
  className,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar: snack } = useSnackbar();
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [isDeleteModalShow, setIsDeleteModalShow] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<number | string | null>(null);
  const [selectedItemUploadId, setSelectedItemUploadId] = useState<number | null>(null);
  const [isCustom, setIsCustom] = useState<boolean | null>(null);
  const [deletedUploadIds, setDeletedUploadIds] = useState<Set<number>>(new Set());

  function openOrDownload(certificate: HistoryItem) {
    let cId = String(certificate.id);
    if (certificate.isCustom) {
      cId = `cc_${certificate.id}`;
    }
    if (certificate.certificateFile) {
      openDownloadWindow(certificate, certificate.userId, undefined, () =>
        snack(t("orders.document_not_found"), { variant: "error" }),
      );
    } else {
      openExportWindow(cId, certificate.userId, () => snack(t("orders.document_not_found"), { variant: "error" }));
    }
  }

  function showDeleteModal(id: number | string | null, uploadId: number | null | undefined, isCustom: boolean) {
    setSelectedItemId(id);
    if (uploadId) {
      setSelectedItemUploadId(uploadId);
    }
    setIsCustom(isCustom);
    setIsDeleteModalShow(true);
  }

  async function confirmDeleteModal(isConfirmed: boolean) {
    if (isConfirmed && selectedItemUploadId !== null) {
      const { ok } = await request(`/user/${userId}/certificates/${selectedItemUploadId}?custom=${isCustom}`, "DELETE");
      if (ok) {
        snack(t("course_history.deleted"), { variant: "success" });
        // Track deleted upload ID to filter it out
        setDeletedUploadIds((prev) => new Set(prev).add(selectedItemUploadId));
        // Trigger compliance recalculation (without cache)
        if (onDeleteSuccess) {
          onDeleteSuccess();
        }
      } else {
        snack(t("course_history.failed_to_delete"), { variant: "error" });
      }
    }
    setIsDeleteModalShow(false);
  }

  useEffect(() => {
    const historyItems = HistoryService.getHistory(certificate, certificateMap, userId);
    // Filter out any items that were deleted in this session
    const filteredItems = historyItems.filter((item) => !deletedUploadIds.has(item.uploadId as number));
    setHistoryItems(filteredItems);
  }, [certificate, certificateMap, userId, deletedUploadIds]);

  return (
    <Dialog open={true} onClose={onClose} maxWidth="lg" className={className}>
      <DialogTitle>{t("course_history.title")}</DialogTitle>
      <DialogContent>
        {historyItems.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
              }}
            >
              {t("course_history.no_history")}
            </Typography>
          </Box>
        ) : (
          <TableComponent
            header={[
              { title: t("course_history.id"), style: { width: "10%" } },
              { title: t("course_history.course_name"), style: { width: "30%" } },
              { title: t("course_history.completion_date"), style: { width: "20%" } },
              { title: t("course_history.valid_until"), style: { width: "20%" } },
              { title: t("course_history.uploaded_on") },
              { title: t("course_history.approved_on") },
              { title: t("course_history.approved_by") },
              { title: t("course_history.export_file"), style: { width: "20%" } },
            ]}
            items={historyItems}
            renderChildren={(item: HistoryItem, index: number) => (
              <TableRow key={`${item.id}-${index}`}>
                <TableCell>{item.isCustom ? `cc_${item.id}` : item.id}</TableCell>
                <TableCell>{item.name}</TableCell>
                <TableCell>{formatDate(item.completedDate)}</TableCell>
                <TableCell>
                  {item.validUntil ? formatDate(item.validUntil) : CertificateStatusType.NEVER_EXPIRE}
                </TableCell>
                <TableCell>{item.uploadedAt}</TableCell>
                <TableCell>{item.approvedAt}</TableCell>
                <TableCell>{item.approvedBy || ""}</TableCell>
                <TableCell>
                  <Button
                    title={t("course_history.download_certificate")}
                    icon={<GetAppIcon />}
                    onClick={() => openOrDownload(item)}
                  />
                  {item.certificateFile && item.uploadId && (
                    <WithAnyRole roles={[COMPUTED_ROLES.SUPER_ADMIN, ROLE_CODES.NOMINATED_PERSON_CREW_TRAINING]}>
                      <Button
                        title={t("course_history.delete_upload")}
                        icon={<DeleteIcon />}
                        onClick={() => showDeleteModal(item.id, item.uploadId, item.isCustom)}
                      />
                    </WithAnyRole>
                  )}
                </TableCell>
              </TableRow>
            )}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()}>{t("course_history.close")}</Button>
      </DialogActions>

      {isDeleteModalShow && selectedItemId && (
        <Confirm title={t("course_history.delete")} onConfirm={confirmDeleteModal}>
          <p>{t("course_history.confirm_delete")}</p>
        </Confirm>
      )}
    </Dialog>
  );
};

export default CourseHistory;
